import {
  autoUpdate,
  offset as offsetMiddleware,
  flip as flipMiddleware,
  shift as shiftMiddleware,
  useFloating,
  useInteractions,
  useRole,
  useDismiss,
  useHover,
  useClick,
  useFocus,
  useFloatingNodeId,
  Placement,
  OffsetOptions,
  UseRoleProps,
  FlipOptions,
  UseDismissProps,
  UseHoverProps,
  UseFocusProps,
  UseClickProps,
} from '@floating-ui/react'
import { useCallback, useState } from 'react'
import { useUpdateEffect } from 'react-use'

type UsePopupParams = {
  opened?: boolean
  defaultOpened?: boolean
  onOpenedChange?: (opened: boolean) => void
  placement?: Placement
  offset?: OffsetOptions
  flip?: FlipOptions
  hover?: UseHoverProps
  click?: UseClickProps
  focus?: UseFocusProps
  role?: UseRoleProps
  dismiss?: UseDismissProps
}

// eslint-disable-next-line import/prefer-default-export
export function usePopup({
  opened,
  defaultOpened,
  onOpenedChange,
  placement,
  offset,
  flip,
  hover: hoverProps = {
    enabled: false,
  },
  click: clickProps = {
    enabled: true,
  },
  focus: focusProps = {
    enabled: false,
  },
  dismiss: dismissProps,
  role: roleProps,
}: UsePopupParams = {}) {
  const [isOpened, setIsOpened] = useState(() => opened ?? defaultOpened ?? false)

  useUpdateEffect(() => {
    if (typeof opened !== 'undefined') {
      setIsOpened(opened)
    }
  }, [opened])

  const handleOnOpenedChange = useCallback(
    (v: boolean) => {
      if (typeof opened === 'undefined') {
        setIsOpened(v)
      }

      onOpenedChange?.(v)
    },
    [opened, onOpenedChange]
  )

  const nodeId = useFloatingNodeId()

  const { refs, context, floatingStyles, ...floatingProps } = useFloating({
    nodeId,
    placement,
    transform: true,
    open: isOpened,
    onOpenChange: handleOnOpenedChange,
    whileElementsMounted: autoUpdate,
    middleware: [offsetMiddleware(offset), flipMiddleware(flip), shiftMiddleware()],
  })

  const hover = useHover(context, hoverProps)
  const click = useClick(context, clickProps)
  const focus = useFocus(context, focusProps)
  const dismiss = useDismiss(context, dismissProps)
  const role = useRole(context, roleProps)

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([hover, click, focus, dismiss, role])

  return {
    ...floatingProps,
    isOpened,
    setIsOpened: handleOnOpenedChange,
    nodeId,
    refs,
    context,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
  }
}
